"use client";

import * as React from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { cn } from "../../lib/utils";
import { triggerHaptic } from "../../lib/haptics";

export type RouteTabItem = {
  value: string;
  label: string;
  href: string;
  icon?: ReactNode;
};

export type RouteTabsProps = {
  value: string;
  items: RouteTabItem[];
  className?: string;
  listClassName?: string;
};

export function RouteTabs({
  value,
  items,
  className = "space-y-4",
  listClassName = "w-full justify-start overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
}: RouteTabsProps) {
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const itemRefs = React.useRef<Array<HTMLAnchorElement | null>>([]);
  const [optimisticValue, setOptimisticValue] = React.useState(value);
  const [indicatorStyle, setIndicatorStyle] = React.useState<{
    width: number;
    x: number;
    ready: boolean;
  }>({
    width: 0,
    x: 0,
    ready: false,
  });

  React.useEffect(() => {
    setOptimisticValue(value);
  }, [value]);

  const updateIndicator = React.useCallback(() => {
    const activeItem = itemRefs.current.find((item) => item?.dataset.active === "true");
    if (!activeItem) return;

    const nextWidth = activeItem.offsetWidth;
    const nextX = activeItem.offsetLeft;

    setIndicatorStyle((current) => {
      if (current.ready && current.width === nextWidth && current.x === nextX) {
        return current;
      }

      return {
        width: nextWidth,
        x: nextX,
        ready: true,
      };
    });
  }, []);

  React.useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(updateIndicator);
    return () => window.cancelAnimationFrame(frame);
  }, [items, optimisticValue, updateIndicator]);

  React.useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      updateIndicator();
    });

    observer.observe(list);

    itemRefs.current.forEach((item) => {
      if (item) observer.observe(item);
    });

    return () => observer.disconnect();
  }, [items, updateIndicator]);

  return (
    <div className={cn("group/tabs flex flex-col gap-2", className)}>
      <div
        ref={listRef}
        role='tablist'
        aria-orientation='horizontal'
        className={cn(
          "relative inline-flex w-fit min-w-0 max-w-full items-end gap-8 border-b border-border bg-transparent p-0 text-muted-foreground",
          listClassName,
        )}
      >
        {items.map((item, index) => {
          const active = item.value === optimisticValue;

          return (
            <Link
              key={item.value}
              ref={(node) => {
                itemRefs.current[index] = node;
              }}
              to={item.href}
              role='tab'
              aria-selected={active}
              aria-current={active ? "page" : undefined}
              data-active={active ? "true" : "false"}
              onClick={(event) => {
                if (
                  event.button !== 0 ||
                  event.metaKey ||
                  event.ctrlKey ||
                  event.altKey ||
                  event.shiftKey ||
                  event.defaultPrevented
                ) {
                  return;
                }

                if (item.value !== optimisticValue) triggerHaptic("selection");
                setOptimisticValue(item.value);
              }}
              className={cn(
                // Mobile-first: slightly larger text (15px) and a roomier gap
                // to the underline (pb-4); tightened to the compact desktop sizes
                // (14px, pb-3) at md. Height follows the content, not a fixed box.
                "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring relative inline-flex h-full flex-none items-center justify-center gap-1.5 whitespace-nowrap border-0 bg-transparent px-0 pt-0 pb-4 text-[0.9375rem] font-medium text-foreground/50 outline-none focus-visible:ring-[3px] focus-visible:outline-1 md:pb-3 md:text-sm",
                "hover:text-foreground/75 data-[active=true]:text-foreground",
              )}
            >
              {item.icon ? <span aria-hidden>{item.icon}</span> : null}
              <span>{item.label}</span>
            </Link>
          );
        })}
        <span
          aria-hidden='true'
          className={cn(
            "pointer-events-none absolute bottom-0 left-0 h-0.5 rounded-full bg-foreground",
            indicatorStyle.ready ? "opacity-100" : "opacity-0",
          )}
          style={{
            width: `${indicatorStyle.width}px`,
            transform: `translateX(${indicatorStyle.x}px)`,
          }}
        />
      </div>
    </div>
  );
}
